/**
 * HeroSection Component
 * Full-screen video background hero with bold typography
 */

import { useState } from 'react';
import { motion } from 'motion/react';
import {
  IconArrowRight,
  IconShippingFast,
  IconShieldCheck,
  IconSupport,
} from '../ui/Icons';

const VIDEO_URL = 'https://tr9ath-e6.myshopify.com/cdn/shop/videos/c/vp/061bcad417c44317a34f717365502d50/061bcad417c44317a34f717365502d50.HD-1080p-7.2Mbps-62330069.mp4?v=0';
const POSTER_URL = 'https://tr9ath-e6.myshopify.com/cdn/shop/files/preview_images/061bcad417c44317a34f717365502d50.thumbnail.0000000000.jpg?v=1762859217&width=1920';

const trustPoints = [
  { icon: IconShippingFast, text: 'Free Next-Day Delivery' },
  { icon: IconShieldCheck, text: '2-Year Warranty' },
  { icon: IconSupport, text: 'UK Support' },
];

export function HeroSection() {
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <section className="relative h-[100vh] min-h-[600px] max-h-[1000px] flex items-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0">
        {/* Poster Image (shows while video loads) */}
        <img
          src={POSTER_URL}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-0' : 'opacity-100'
          }`}
          aria-hidden="true"
        />

        {/* Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={POSTER_URL}
          onLoadedData={() => setVideoLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>

        {/* Gradient Overlays for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-surface-black via-surface-black/80 to-surface-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface-black via-transparent to-surface-black/60" />

        {/* Subtle vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.3)_100%)]" />
      </div>

      {/* Content */}
      <div className="container-wide relative z-10">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="inline-flex items-center gap-3 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full text-sm text-white/90">
              <span className="w-2 h-2 bg-brand-orange rounded-full animate-pulse" />
              Professional Headset Specialist
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold text-white leading-[0.95] tracking-tight"
          >
            PROFESSIONAL
            <br />
            AUDIO.
            <br />
            <span className="text-brand-orange">PREMIUM</span>
            <br />
            <span className="text-brand-orange">HEADSETS.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed"
          >
            Jabra, Poly and Axtel — all in one place. Headsets for offices,
            call centres, and remote teams.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="/collections/all-products"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-brand-orange text-white font-semibold rounded-xl hover:bg-brand-orange-dark transition-all duration-300 hover:shadow-2xl hover:shadow-brand-orange/30 hover:-translate-y-0.5"
            >
              Shop Now
              <IconArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="/products"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300"
            >
              Best Sellers
            </a>
          </motion.div>

          {/* Trust Points */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 flex flex-wrap gap-6"
          >
            {trustPoints.map((point, i) => (
              <motion.div
                key={point.text}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.8 + i * 0.1 }}
                className="flex items-center gap-2 text-sm text-white/70"
              >
                <point.icon size={18} className="text-brand-orange" />
                {point.text}
              </motion.div>
            ))}
          </motion.div>

          {/* Brand Logos */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-12 pt-8 border-t border-white/10"
          >
            <div className="flex items-center gap-8">
              <span className="text-xs uppercase tracking-widest text-white/40">
                Brands We Stock
              </span>
              <div className="flex items-center gap-6">
                {['Jabra', 'Poly', 'Axtel'].map((brand) => (
                  <a
                    key={brand}
                    href={`/collections/${brand.toLowerCase()}`}
                    className="text-xl font-display font-bold text-white/50 hover:text-brand-orange transition-colors duration-300"
                  >
                    {brand}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-widest text-white/30">Scroll</span>
          <svg
            className="w-6 h-6 text-white/30"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
}
